import React, { memo, useEffect, useState } from "react";
import { Modal, Button, Dropdown, Form, Tabs, Tab } from "react-bootstrap";
import "./styles.scss";
import { getTopicScores } from "../../../utils/scores";
import Loader from "../../../components/Loader";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { getSingalClassroomData } from "../../../utils/classrooms";
import BsFilter from "../../../constants/icons/Bs/BsFilter";
import { BsBook } from "../../../constants/icons/Bs/BsBook";
import {useSelector} from "react-redux";
import {getUserRole} from "../../../store/userRole";
import InfoTooltip from "./InfoTooltip";

const Scores = ({ classroomId, isMobileView, studentId }) => {
  const navigate = useNavigate();
  const roleOfTheUser = useSelector(getUserRole);
  const [showModal, setShowModal] = useState(false);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [topicPracticeSets, setTopicPracticeSets] = useState([]);
  const [topicTests, setTopicTests] = useState([]);
  const [topicScores, setTopicScores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subjects, setSubjects] = useState([]);
  const [filteredSubjects, setFilteredSubjects] = useState([]);
  const [filteredTopicScores, setFilteredTopicScores] = useState([]);

  // Fetch classroom details to get subjects
  useEffect(() => {
    const getClassroomDetails = async () => {
      try {
        const response = await getSingalClassroomData(classroomId);
        if (response?.data?.classRoom?.course?.subjects) {
          const subjectOptions = response.data.classRoom.course.subjects
            .filter((subject) => !subject?.isHidden)
            .map((subject) => ({
              value: subject._id,
              label: subject.name,
            }));
          setSubjects(subjectOptions);
        }
      } catch (error) {
        console.error("Error fetching classroom details:", error);
        toast.error("Failed to load classroom details");
      }
    };

    if (classroomId) {
      getClassroomDetails();
    }
  }, [classroomId]);

  // Fetch topic scores
  useEffect(() => {
    const fetchTopicScores = async () => {
      try {
        setLoading(true);
        const response = await getTopicScores(classroomId, studentId);

        if (response.ok) {
          setTopicScores(response.data.topics || []);
        } else {
          toast.error("Failed to load topic scores");
        }
      } catch (error) {
        console.error("Error fetching topic scores:", error);
        toast.error("Failed to load topic scores");
      } finally {
        setLoading(false);
      }
    };

    if (classroomId) {
      fetchTopicScores();
    }
  }, [classroomId, studentId]);

  // Filter topics based on selected subjects
  useEffect(() => {
    if (filteredSubjects.length > 0 && topicScores.length > 0) {
      const filtered = topicScores.filter((topic) =>
        filteredSubjects.includes(topic.subjectId)
      );
      setFilteredTopicScores(filtered);
    } else {
      setFilteredTopicScores(topicScores);
    }
  }, [filteredSubjects, topicScores]);

  const handleFilterChange = (subjectId) => {
    setFilteredSubjects((prevSelected) => {
      const isSelected = prevSelected.includes(subjectId);
      return isSelected
        ? prevSelected.filter((id) => id !== subjectId)
        : [...prevSelected, subjectId];
    });
  };

  const handleTopicClick = (topic) => {
    setSelectedTopic(topic);
    setTopicPracticeSets(topic.latestPracticesDone || []);
    setTopicTests(topic.latestTestsDone || []);
    setShowModal(true);
  };

  const handleViewDetail = (resource, topicId, type) => {
    if (type === "practice") {
      navigate(
        `/student/myClassrooms/${classroomId}/${resource.subjectId}/${topicId}/practice/${resource._id}/${resource.examId}`
      );
    } else if (type === "test") {
      navigate(
        `/student/myClassrooms/${classroomId}/${resource.subjectId}/${topicId}/test/${resource._id}/${resource.examId}`
      );
    }
  };

  const handleReattempt = (resource, topicId, type) => {
    const basePath = roleOfTheUser === "Student" ? "/student" : "";
    if (type === "practice") {
      const attemptType = resource?.type === "writing"
        ? "writingAttempt"
        : resource?.type === "speaking"
          ? "speakingAttempt"
          : "practiceAttempt"
      navigate(
        `${basePath}/myClassrooms/${classroomId}/${resource.subjectId}/${topicId}/practice/${attemptType}/${resource.examId}`
      );
    } else if (type === "test") {
      const attemptType = resource?.type === "writing"
        ? "writingTestAttempt"
        : resource?.type === "speaking"
          ? "speakingTestAttempt"
          : "testAttempt";
      navigate(
        `${basePath}/myClassrooms/${classroomId}/${resource.subjectId}/${topicId}/test/${attemptType}/${resource.examId}`
      );
    }
  };

  if (loading) {
    return <Loader />;
  }

  return (
    <div
      style={{ margin: "10px", paddingBottom: isMobileView ? "20px" : "" }}
      id="Scores"
    >
      {/* Subject Filter */}
      {subjects.length > 0 && (
        <div className="filter-section">
          <div className="p-4">
            <div className="d-flex gap-3">
              <Dropdown className="filter-dropdown">
                <Dropdown.Toggle variant="light" className="filter-button">
                  <div className="filter-button-content">
                    <BsFilter className="filter-icon" />
                    <span className="filter-text ms-2">Filter by Subject</span>
                    {filteredSubjects.length > 0 && (
                      <span className="filter-badge ms-2">
                        {filteredSubjects.length}
                      </span>
                    )}
                  </div>
                </Dropdown.Toggle>
                <Dropdown.Menu className="filter-menu" align="end">
                  <div className="filter-menu-header">
                    <div className="filter-menu-title">
                      <BsFilter className="filter-menu-icon" />
                      <h5>Filter Subjects</h5>
                    </div>
                    {filteredSubjects.length > 0 && (
                      <Button
                        variant="link"
                        size="sm"
                        className="clear-all"
                        onClick={() => setFilteredSubjects([])}
                      >
                        Clear All
                      </Button>
                    )}
                  </div>
                  <div className="filter-menu-divider" />
                  <div className="filter-options">
                    {subjects.map((subject) => (
                      <div key={subject.value} className="filter-option">
                        <Form.Check
                          type="checkbox"
                          id={`subject-${subject.value}`}
                          label={subject.label}
                          checked={filteredSubjects.includes(subject.value)}
                          onChange={() => handleFilterChange(subject.value)}
                          className="custom-checkbox"
                        />
                      </div>
                    ))}
                  </div>
                </Dropdown.Menu>
              </Dropdown>
            </div>
          </div>
        </div>
      )}

      {/* Topic Scores Grid */}
      <div className="col-12 col-lg-12 mb-4">
        <div className="p-4">
          <div className="excel-grid">
            {/* Header Row */}
            <div className="grid-header">
              <div className="grid-cell">Topic Name</div>
              <div className="grid-cell" style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                justifyContent: 'flex-end'
              }}>Score <InfoTooltip text={"Each topic's percentage is your combined accuracy across the practice sets and tests you've completed. Unattempted resources are never part of the calculation."} /></div>
            </div>
            {/* Data Rows */}
            {filteredTopicScores.map((topic) => (
              <div key={topic._id} className="grid-row">
                <div
                  className="grid-cell clickable"
                  onClick={() => handleTopicClick(topic)}
                >
                  {topic.name}
                </div>
                <div className="grid-cell">
                  {parseFloat(topic.score || 0).toFixed(1)}%
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Resources Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{selectedTopic?.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Tabs defaultActiveKey="practices" className="mb-3">
            <Tab
              eventKey="practices"
              title={
                <div className="d-flex align-items-center gap-2">
                  <BsBook className="tab-icon" />
                  <span>Practice Sets ({topicPracticeSets.length})</span>
                </div>
              }
            >
              <div className="practice-sets-list">
                {topicPracticeSets.length > 0 ? (
                  topicPracticeSets.map((set) => (
                    <div key={set._id} className="practice-set-item">
                      <div className="practice-set-info">
                        <h6>{set.name}</h6>
                        <div className="score-info">
                          {set?.type ? 'Band:' : 'Score:'}{" "}
                          {set?.type ? set?.score ?? 0 : parseFloat(
                            (set.obtainedPoints / set.totalPoints) * 100 || 0
                          ).toFixed(1)}
                          {set?.type ? '' : `% (${set.totalPoints} questions)`}
                        </div>
                      </div>
                      <div className="practice-set-actions">
                        {!set?.type && (
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() =>
                              handleViewDetail(
                                set,
                                selectedTopic?._id,
                                "practice"
                              )
                            }
                          >
                            View Detail
                          </Button>
                        )}
                        <Button
                          variant="primary"
                          size="sm"
                          className="m-0"
                          onClick={() =>
                            handleReattempt(set, selectedTopic?._id, "practice")
                          }
                        >
                          Reattempt
                        </Button>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="no-resources">No practice sets available</div>
                )}
              </div>
            </Tab>
            <Tab
              eventKey="tests"
              title={
                <div className="d-flex align-items-center gap-2">
                  <BsBook className="tab-icon" />
                  <span>Tests ({topicTests.length})</span>
                </div>
              }
            >
              <div className="practice-sets-list">
                {topicTests.length > 0 ? (
                  topicTests.map((test) => (
                    <div key={test._id} className="practice-set-item">
                      <div className="practice-set-info">
                        <h6>{test.name}</h6>
                        <div className="score-info">
                          {test?.type ? 'Band:' : 'Score:'}{" "}
                          {test?.type ? test?.score ?? 0 : parseFloat(
                            (test.obtainedPoints / test.totalPoints) * 100 || 0
                          ).toFixed(1)}
                          {test?.type ? '' : `% (${test.totalPoints} questions)`}
                        </div>
                      </div>
                      <div className="practice-set-actions">
                        {!test?.type && (
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() =>
                              handleViewDetail(test, selectedTopic?._id, "test")
                            }
                          >
                            View Detail
                          </Button>
                        )}
                        <Button
                          variant="primary"
                          size="sm"
                          className="m-0"
                          onClick={() =>
                            handleReattempt(test, selectedTopic?._id, "test")
                          }
                        >
                          Reattempt
                        </Button>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="no-resources">No tests available</div>
                )}
              </div>
            </Tab>
          </Tabs>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default memo(Scores);
